import { withRouter, RouteComponentProps } from 'react-router-dom';
import React from 'react';
import type { ParsedQs } from 'qs';
import type { QueryObject } from 'interface/queryParam';

import history from 'utils/history';
import { parse, stringify } from 'utils/queryParam';

import FilterPill from './FilterPill';

type StatusMap = Record<string, string>;
type Summary = Record<string, number> & { approved?: number; pending?: number; rejected?: number };

interface OwnProps {
  statusLabels: Record<string, React.ReactNode>;
  statuses: StatusMap;
  colorMap: Record<string, string>;
  summary: Summary;
  onChange: (status: string) => void;
}

type Props = OwnProps & RouteComponentProps;
type QueryValue = string | string[] | ParsedQs | ParsedQs[] | undefined;
type FilterQuery = { page?: QueryValue; status?: QueryValue };

const FilterPills: React.FC<Props> = (props) => {
  const { statusLabels, statuses, colorMap, summary, onChange } = props;

  const totalCount = Object.values(summary).reduce<number>((acc, value) => {
    if (typeof value !== 'number') {
      return acc;
    }
    return acc + value;
  }, 0);

  const toStringValue = (value: QueryValue): string | undefined => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') return value[0];
    return undefined;
  };

  const queryParam = parse(props.location.search) as FilterQuery;
  const activeFilter = toStringValue(queryParam.status) || statuses.ALL;

  const handleFilterClick = (selectedFilter?: string) => {
    const nextQuery: QueryObject = {};

    Object.entries(queryParam).forEach(([key, queryValue]) => {
      const normalizedValue = toStringValue(queryValue);
      if (normalizedValue !== undefined) {
        nextQuery[key] = normalizedValue;
      }
    });

    delete nextQuery.page;

    updateUrl({ ...nextQuery, status: selectedFilter });

    onChange(selectedFilter || statuses.ALL);
  };

  const updateUrl = (value: QueryObject) => {
    const queryParam = stringify('', value);

    history.push({
      pathname: props.location.pathname,
      search: queryParam,
    });
  };

  return (
    <>
      {totalCount ? (
        <FilterPill
          notification={totalCount}
          isSmall={true}
          label={statusLabels[statuses.ALL]}
          color={colorMap[statuses.ALL]}
          isActive={activeFilter === statuses.ALL}
          handleClick={() => handleFilterClick()}
        />
      ) : null}

      {summary.approved > 0 && (
        <FilterPill
          notification={summary.approved}
          isSmall={true}
          label={statusLabels[statuses.APPROVED]}
          color={colorMap[statuses.APPROVED]}
          isActive={activeFilter === statuses.APPROVED}
          handleClick={() => handleFilterClick(statuses.APPROVED)}
        />
      )}

      {summary.pending > 0 && (
        <FilterPill
          notification={summary.pending}
          isSmall={true}
          label={statusLabels[statuses.PENDING]}
          color={colorMap[statuses.PENDING]}
          isActive={activeFilter === statuses.PENDING}
          handleClick={() => handleFilterClick(statuses.PENDING)}
        />
      )}

      {summary.rejected > 0 && (
        <FilterPill
          notification={summary.rejected}
          isSmall={true}
          label={statusLabels[statuses.REJECTED]}
          color={colorMap[statuses.REJECTED]}
          isActive={activeFilter === statuses.REJECTED}
          handleClick={() => handleFilterClick(statuses.REJECTED)}
        />
      )}
    </>
  );
};

export default withRouter(FilterPills);
